import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { createUseStyles } from '@Sage/hoi-poi-ui';
import { generateUUID } from '@Sage/ssm-utils';

import { useWeb5Context } from 'lib/Web5Context';
import {
    getCopilotStreamAnswer,
    getCopilotStreamAnswerWithFunctions,
    INTERACTION_TYPES,
} from 'lib/services/copilot';
import copyToClipboard from 'utils/copy';

import { CopilotContext } from '../Context';
import useBuildAnswer from '../hooks/useBuildAnswer';
import useChatStream from '../hooks/useChatStream';
import { COPILOT_BAR_LOADING_STATES } from '../hooks/constants';
import { createToolDonePhrase, createToolInitPhrase } from '../hooks/utils';
import AgentSwitcher from './components/AgentSwitcher';
import Chat from './components/Chat';
import ChatToolbar from './components/ChatToolbar';

import styles from './styles';

const useStyles = createUseStyles(styles, 'CopilotChat');

const CopilotChat = memo(
    ({
        cb, // Callbacks for the Copilot chat events
        chatId,
        chatSuggestions,
        className,
        CustomActionsComponent,
        defaultFirstInteraction,
        enableVoiceInput = false,
        handleEmail,
        handleFeedback,
        handleVote,
        interactions = [],
        loadingPhrase,
        overrides = {},
        session,
    }) => {
        // ========================================
        // CONTEXT & CUSTOM HOOKS
        // ========================================
        const classes = useStyles();
        const { t } = useWeb5Context();
        const { cleanChatTextForClipboard } = useBuildAnswer();

        const {
            chats,
            clearChat,
            functionRegistry,
            getStreamReader,
            getMethodsForCopilot,
            removeInteraction,
            setChatSession,
            setInteraction,
            setIsInitialized,
            setStreamReader,
        } = useContext(CopilotContext);

        // ========================================
        // REFS
        // ========================================
        const lastAudioDataRef = useRef(null);
        const lastTextMessageRef = useRef(null);
        const onSubmitChatRef = useRef(null);

        // ========================================
        // LOCAL STATE
        // ========================================
        const defaultLoadingPhrase = useMemo(() => t('label_danai_thinking_v3'), [t]);
        const [dynamicLoadingPhrase, setDynamicLoadingPhrase] = useState(
            loadingPhrase ?? defaultLoadingPhrase,
        );
        const [errorState, setErrorState] = useState(null);

        // ========================================
        // DERIVED STATE & MEMOIZATION
        // ========================================
        const chatState = useMemo(() => chats?.[chatId] ?? {}, [chats, chatId]);
        const { chat, chatSession, isInitialized, reports, tables } = chatState;

        const finalSession = useMemo(() => {
            return {
                ...session,
                inputAudioFormat: enableVoiceInput ? 'pcm16' : undefined,
            };
        }, [session, enableVoiceInput]);

        // ========================================
        // EVENT HANDLERS & CALLBACKS
        // ========================================

        // Callback to handle loading state changes from useChatStream (for error handling)
        const handleLoadingStateChange = useCallback((props) => {
            if (props?.loadingState === COPILOT_BAR_LOADING_STATES.PROCESSING_ERROR) {
                setErrorState(COPILOT_BAR_LOADING_STATES.PROCESSING_ERROR);
            } else if (
                props?.loadingState === COPILOT_BAR_LOADING_STATES.IDLE ||
                props?.loadingState === COPILOT_BAR_LOADING_STATES.LOADING ||
                props?.loadingState === COPILOT_BAR_LOADING_STATES.COMPLETED
            ) {
                setErrorState(null);
            }
        }, []);

        /**
         * Helper to update dynamic phrases based on callback result
         * @param {Object|undefined} result - The result from the callback
         * @param {string} result.phrase - Phrase to use
         */
        const updatePhraseFromResult = useCallback((result) => {
            if (!result?.phrase) return;

            setDynamicLoadingPhrase(result.phrase);
        }, []);

        // Wrapper handler for onToolInit that supports dynamic phrases update
        const handleToolInit = useCallback(
            (tool) => {
                // Call custom callback if provided, otherwise use default
                const result = cb?.onToolInit
                    ? cb.onToolInit(tool)
                    : createToolInitPhrase({ t, tool });

                updatePhraseFromResult(result);
            },
            [cb, t, updatePhraseFromResult],
        );

        // Wrapper handler for onToolDone that supports dynamic phrases update
        const handleToolDone = useCallback(
            (tool) => {
                // Call custom callback if provided, otherwise use default
                const result = cb?.onToolDone
                    ? cb.onToolDone(tool)
                    : createToolDonePhrase({ t, tool });

                updatePhraseFromResult(result);
            },
            [cb, t, updatePhraseFromResult],
        );

        // ========================================
        // CUSTOM HOOKS
        // ========================================
        const {
            isLoadingAnswer,
            setIsLoadingAnswer,
            refs: { answerInteractionIdRef, questionRef },
            onAnswer,
            onData,
            onError,
            onFinish,
            onAction,
            onArtifact,
            onForm,
            onSetChatSession,
            onTextDone,
            cancelSequence,
            onSubmitChat,
            onSubmitVoice,
            getQuestionRef,
            onResponseDone,
        } = useChatStream({
            chat,
            chatId,
            chatSession,
            functionRegistry,
            interactions,
            getStreamReader,
            onActionCallback: cb?.onAction,
            onAnswerCallback: cb?.onAnswer,
            onArtifactCallback: cb?.onArtifact,
            onDataCallback: cb?.onData,
            onErrorCallback: cb?.onError,
            onFinishCallback: cb?.onFinish,
            onFormCallback: cb?.onForm,
            onResponseDoneCallback: cb?.onResponseDone,
            onToolDoneCallback: handleToolDone,
            onToolInitCallback: handleToolInit,
            session: finalSession,
            setChatSession,
            removeInteraction,
            setCopilotBarProps: handleLoadingStateChange,
            setInteraction,
            setStreamReader,
            t,
        });

        const isDanaWritting = useMemo(() => isLoadingAnswer, [isLoadingAnswer]);

        useEffect(() => {
            onSubmitChatRef.current = onSubmitChat;
        }, [onSubmitChat]);

        const handleClearChat = useCallback(() => {
            cancelSequence();
            clearChat({ chatId });
            setDynamicLoadingPhrase(defaultLoadingPhrase);
            cb?.onClearChat?.();
        }, [cancelSequence, cb, chatId, clearChat, defaultLoadingPhrase]);

        const handleRecordingError = useCallback((error) => {
            console.error('Voice recording error:', error);
            setErrorState(COPILOT_BAR_LOADING_STATES.RECORDING_ERROR);
        }, []);

        const handleDismissError = useCallback(() => {
            setErrorState(null);
        }, []);

        const handleTextSubmit = useCallback(
            (suggestion) => {
                // Store text for potential retry (use suggestion or current input)
                const textToStore = suggestion || questionRef.current || '';
                lastTextMessageRef.current = textToStore;
                // Clear audio data since this is a text submission
                lastAudioDataRef.current = null;
                // Clear any previous error state
                setErrorState(null);
                onSubmitChat(suggestion);
            },
            [onSubmitChat, questionRef],
        );

        const handleVoiceSubmit = useCallback(
            (audioData) => {
                // Store audio data for potential retry
                lastAudioDataRef.current = audioData;
                // Clear text data since this is a voice submission
                lastTextMessageRef.current = null;
                // Clear any previous error state
                setErrorState(null);
                onSubmitVoice(audioData);
            },
            [onSubmitVoice],
        );

        const onCopy = useCallback(
            (value) => {
                if (!value.content) return;

                const text = cleanChatTextForClipboard({
                    value: value.content,
                    tables,
                    reports,
                });
                copyToClipboard(text, t);
            },
            [cleanChatTextForClipboard, reports, t, tables],
        );

        // ========================================
        // EFFECTS
        // ========================================
        useEffect(() => {
            if (!isInitialized) {
                setIsInitialized({ chatId, value: true });

                if (defaultFirstInteraction?.text) {
                    setInteraction({
                        chatId,
                        id: generateUUID(),
                        isFinished: true,
                        isQuestion: false,
                        isError: false,
                        isLoading: false,
                        disableVote: true,
                        ...defaultFirstInteraction,
                    });
                }

                if (interactions.length > 0 && !answerInteractionIdRef.current) {
                    setIsLoadingAnswer(true);
                    const questionId = generateUUID();
                    answerInteractionIdRef.current = generateUUID();

                    // Handle GoldenMinute click on CTA prompts
                    interactions
                        .filter(({ item }) => item.type === INTERACTION_TYPES.MESSAGE)
                        .forEach(({ item }) => {
                            setInteraction({
                                chatId,
                                id: generateUUID(),
                                text: item.content.text,
                                isFinished: true,
                                isQuestion: true,
                                isError: false,
                                isLoading: false,
                                parentId: questionId,
                            });
                        });

                    setInteraction({
                        chatId,
                        id: answerInteractionIdRef.current,
                        text: '',
                        isLoading: true,
                        parentId: questionId,
                    });

                    getCopilotStreamAnswer({
                        interactions,
                        onAction,
                        onArtifact,
                        onError: onError(questionId),
                        onFinish,
                        onForm,
                        onResponseDone,
                        onSetReader: (reader) => setStreamReader({ chatId, reader }),
                        onTextDone,
                        onToolDone: handleToolDone,
                        onToolInit: handleToolInit,
                        session: finalSession,
                        setData: onData,
                        setSession: onSetChatSession,
                        setText: onAnswer,
                    }).finally(() => {
                        setIsLoadingAnswer(false);
                    });
                }
            }
        }, [
            answerInteractionIdRef,
            chatId,
            defaultFirstInteraction,
            finalSession,
            handleToolDone,
            handleToolInit,
            interactions,
            isInitialized,
            onAction,
            onAnswer,
            onArtifact,
            onData,
            onError,
            onFinish,
            onForm,
            onResponseDone,
            onSetChatSession,
            onTextDone,
            session,
            setIsInitialized,
            setIsLoadingAnswer,
            setInteraction,
            setStreamReader,
        ]);

        useEffect(() => {
            getMethodsForCopilot.registerFeatureMethods(`chat-${chatId}`, {
                onSubmit: (...args) => onSubmitChatRef.current?.(...args),
            });
        }, [getMethodsForCopilot, chatId]);

        // Sync dynamic loading phrase when the prop changes
        useEffect(() => {
            setDynamicLoadingPhrase(loadingPhrase ?? defaultLoadingPhrase);
        }, [defaultLoadingPhrase, loadingPhrase]);

        // ========================================
        // MEMOIZED COMPONENT PROPS
        // ========================================
        const chatProps = useMemo(
            () => ({
                chatId,
                conversationId: chatSession?.id,
                conversation: chat,
                chatSuggestions,
                isDanaWritting,
                loadingPhrase: dynamicLoadingPhrase,
                onCopy,
                onEmail: handleEmail,
                onFeedback: handleFeedback,
                onVote: handleVote,
            }),
            [
                chatId,
                chatSession?.id,
                chat,
                chatSuggestions,
                dynamicLoadingPhrase,
                isDanaWritting,
                onCopy,
                handleEmail,
                handleFeedback,
                handleVote,
            ],
        );

        const chatToolbarProps = useMemo(
            () => ({
                conversation: chat,
                enableVoiceInput,
                errorState,
                getQuestionRef,
                isDanaWritting,
                isLoadingAnswer,
                lastAudioData: lastAudioDataRef.current,
                lastTextMessage: lastTextMessageRef.current,
                onClear: handleClearChat,
                onDismissError: handleDismissError,
                onRecordingError: handleRecordingError,
                onSubmit: handleTextSubmit,
                onVoiceSubmit: handleVoiceSubmit,
            }),
            [
                chat,
                enableVoiceInput,
                errorState,
                getQuestionRef,
                handleClearChat,
                handleDismissError,
                handleRecordingError,
                handleTextSubmit,
                handleVoiceSubmit,
                isDanaWritting,
                isLoadingAnswer,
            ],
        );

        // ========================================
        // RENDER
        // ========================================
        return (
            <div className={classNames(classes.root, className)}>
                {chat && (
                    <div className={classes.chat}>
                        <Chat {...chatProps} />
                    </div>
                )}
                {!overrides?.disableChatInput && (
                    <div className={classes.chatBar}>
                        <div className={classes.chatToolbar}>
                            {!overrides?.disableAgentSwitcher && <AgentSwitcher chatId={chatId} />}
                            {!!CustomActionsComponent && (
                                <CustomActionsComponent menuPlacement="topLeft" />
                            )}
                        </div>
                        <div className={classes.input}>
                            <ChatToolbar {...chatToolbarProps} />
                        </div>
                    </div>
                )}
            </div>
        );
    },
);

export default CopilotChat;

export { getCopilotStreamAnswer, getCopilotStreamAnswerWithFunctions, ChatToolbar };
